import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Tooltip, Grid, Typography, Pagination, Slide, Fade,
    InputAdornment, Snackbar, Alert, Box, CircularProgress, Chip,
    Select, MenuItem, FormControl, InputLabel, TextField,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Pending as PendingIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import {
    APILPOIIList, APILPOIIDelete, APILPOIIStatus, headers,
} from '../../utilities/APIs';
import useCustomToast from '../../hooks/useToast';
import axios from 'axios';
import { formatCreatedAtDate } from '../../utilities/Functions';
import {
    getUserRole, PERMISSIONS, ROLE_PERMISSIONS,
} from '../../utilities/Permission';

// ---------------------------------------------------------------
// SAFE HELPERS (crash-proof against null / undefined / NaN / strings)
// ---------------------------------------------------------------
const formatNumberWithComma = (num) => {
    if (num === undefined || num === null || num === '') return '0';
    const n = Number(num);
    if (Number.isNaN(n)) return '0';
    return n.toLocaleString('en-US');
};

const parseItems = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const decoded = JSON.parse(raw);
            return Array.isArray(decoded) ? decoded : [];
        } catch {
            return [];
        }
    }
    return [];
};

const getCommodityName = (item) =>
    item?.commodityName || item?.commodityId || item?.commodity_id || 'N/A';

const getItemTotal = (item) =>
    (Number(item?.quantity) || 0) * (Number(item?.unitPrice) || 0);

// ---------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------
const LpoTwoOrders = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);
    const [currentOrder, setCurrentOrder] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [updateId, setUpdateId] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const showToast = useCustomToast();
    const [pageState, setPageState] = useState({ pageData: [] });
    const [snackbar, setSnackbar] = useState({
        open: false, message: '', severity: 'success',
    });
    const [statusFormData, setStatusFormData] = useState({ status: '' });
    const [userRole, setUserRole] = useState('');

    // --- PERMISSIONS ---
    useEffect(() => {
        setUserRole(getUserRole());
    }, []);

    const hasPermission = (permission) =>
        ROLE_PERMISSIONS[userRole]?.includes(permission) || false;

    // --- FETCH ---
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(APILPOIIList, { headers });

            const rawList = Array.isArray(response.data?.data)
                ? response.data.data
                : [];

            // Normalize every order: numeric fields -> numbers, items -> array
            const data = rawList.reverse().map((order) => ({
                ...order,
                subtotal: Number(order.subtotal) || 0,
                tax: Number(order.tax) || 0,
                shipping: Number(order.shipping) || 0,
                other: Number(order.other) || 0,
                total_ugx: Number(order.total_ugx) || 0,
                total_usd: Number(order.total_usd) || 0,
                exchange_rate: Number(order.exchange_rate) || 0,
                items: parseItems(order.items).map((it) => ({
                    ...it,
                    quantity: Number(it?.quantity) || 0,
                    unitPrice: Number(it?.unitPrice) || 0,
                    total: Number(it?.total) || 0,
                })),
            }));

            setPageState((prev) => ({ ...prev, pageData: data }));
        } catch (err) {
            let errorMessage = 'Failed to load orders data';
            if (err.response) {
                errorMessage = err.response.data?.message || err.message;
            } else if (err.code === 'ERR_NETWORK') {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast('Error', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- FILTERING ---
    const filteredOrders = pageState.pageData.filter((order) => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            order.lpo_number?.toLowerCase().includes(term) ||
            order.buyer_name?.toLowerCase().includes(term) ||
            order.buyer_country?.toLowerCase().includes(term) ||
            order.requisitioner_name?.toLowerCase().includes(term) ||
            order.supplier_vendor_name?.toLowerCase().includes(term) ||
            order.supplier_country?.toLowerCase().includes(term);

        const matchesStatus =
            statusFilter === 'all' || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // --- PAGINATION ---
    const count = Math.ceil(filteredOrders.length / rowsPerPage);
    const indexOfLastRow = page * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredOrders.slice(indexOfFirstRow, indexOfLastRow);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    // --- DIALOG OPENERS ---
    const handleOpenView = (order) => {
        setCurrentOrder(order);
        setOpenView(true);
    };

    const handleOpenDelete = (order) => {
        setCurrentOrder(order);
        setUpdateId(order.id);
        setOpenDelete(true);
    };

    const handleOpenStatus = (order) => {
        setCurrentOrder(order);
        setUpdateId(order.id);
        setStatusFormData({ status: order.status || 'pending' });
        setOpenStatus(true);
    };

    const handleClose = () => {
        setOpenView(false);
        setOpenDelete(false);
        setOpenStatus(false);
        setCurrentOrder(null);
    };

    const handleRefresh = () => {
        fetchData();
        setSnackbar({
            open: true, message: 'Orders data refreshed', severity: 'info',
        });
    };

    const handleCloseSnackbar = () =>
        setSnackbar((prev) => ({ ...prev, open: false }));

    // --- STATUS UPDATE ---
    const handleStatusChange = (e) => {
        const { name, value } = e.target;
        setStatusFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleStatusUpdate = async () => {
        try {
            const formData = new FormData();
            formData.append('status', statusFormData.status);

            const response = await axios.post(
                `${APILPOIIStatus}/${updateId}`,
                formData,
                { headers }
            );

            if (response.data.status === 'success') {
                handleClose();
                fetchData();
                showToast(
                    'Success',
                    `Order status updated to ${statusFormData.status}`,
                    'success'
                );
            } else {
                showToast('Error', 'Failed to update status', 'error');
            }
        } catch (err) {
            handleAxiosError(err);
        }
    };

    // --- DELETE ---
    const handleDelete = async () => {
        try {
            const formData = new FormData();
            formData.append('updateId', updateId);

            const response = await axios.post(APILPOIIDelete, formData, { headers });

            if (response.data.status === 'success') {
                handleClose();
                fetchData();
                showToast('Success', 'Order deleted successfully', 'success');
            } else {
                showToast('Error', 'Failed to delete order', 'error');
            }
        } catch (err) {
            handleAxiosError(err);
        }
    };

    const handleAxiosError = (err) => {
        if (err.response) {
            if (err.response.status === 422) {
                const errors = err.response.data?.errors || {};
                const errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast('Validation Error', errorMessages.join('\n'), 'error');
            } else {
                showToast(
                    'Error',
                    err.response.data?.message || 'An error occurred',
                    'error'
                );
            }
        } else if (err.code === 'ERR_NETWORK') {
            showToast('Connection Error', TEXT_CONNECTION_ERROR, 'error');
        } else {
            showToast('Error', 'An unexpected error occurred', 'error');
        }
    };

    // --- STATUS COLOR ---
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'completed': return 'info';
            default: return 'default';
        }
    };

    // --- LOADING / ERROR STATES ---
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center"
                minHeight="200px" flexDirection="column" gap={2}>
                <CircularProgress />
                <Typography variant="body1">Loading orders...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center"
                minHeight="200px" flexDirection="column" gap={2}>
                <Typography variant="h6" color="error">Error Loading Data</Typography>
                <Typography variant="body2" color="textSecondary">{error}</Typography>
                <Button variant="contained" color="primary" onClick={fetchData}
                    startIcon={<RefreshIcon />}>Retry</Button>
            </Box>
        );
    }

    // ---------------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------------
    return (
        <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
            {/* ---------- FILTERS ---------- */}
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                <Paper elevation={3} style={{
                    padding: isMobile ? '16px' : '24px',
                    borderRadius: '12px', marginBottom: '24px',
                }}>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        <Grid item>
                            <Typography variant="subtitle1">
                                LPO II Orders Total:
                                <Chip
                                    label={formatNumberWithComma(pageState.pageData.length)}
                                    color="primary"
                                    variant="outlined"
                                    style={{ marginLeft: '8px' }}
                                />
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} style={{ marginTop: '8px' }}>
                        <Grid item xs={12} sm={4} md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search orders..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                    ),
                                    style: { borderRadius: '8px' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Status Filter</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                    label="Status Filter"
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <TextField
                                fullWidth
                                select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                variant="outlined"
                                SelectProps={{ native: true }}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={12}
                            style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Tooltip title="Refresh data">
                                <IconButton
                                    onClick={handleRefresh}
                                    style={{
                                        backgroundColor: theme.palette.grey[200],
                                        borderRadius: '8px',
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>
            </Slide>

            {/* ---------- TABLE ---------- */}
            <Fade in={!loading} timeout={500}>
                <div>
                    <TableContainer component={Paper} elevation={3}
                        style={{ borderRadius: '12px', marginBottom: '24px' }}>
                        <Table>
                            <TableHead>
                                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>LPO Number</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Buyer</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Vendor</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Requisitioner</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Total (UGX)</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentRows.length > 0 ? (
                                    currentRows.map((order, index) => (
                                        <StyledTableRow key={order.id}>
                                            <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {order.lpo_number}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{order.buyer_name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {order.buyer_country}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {order.supplier_vendor_name || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {order.supplier_country}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {order.requisitioner_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {order.requisitioner_area}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold" color="primary">
                                                    UGX {formatNumberWithComma(order.total_ugx)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={(order.status || 'pending').toUpperCase()}
                                                    color={getStatusColor(order.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatCreatedAtDate(order.date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    <Tooltip title="View Details">
                                                        <ActionButton
                                                            variant="contained"
                                                            color="info"
                                                            size="small"
                                                            onClick={() => handleOpenView(order)}
                                                            style={{ backgroundColor: '#17a2b8' }}
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Update Status">
                                                        <ActionButton
                                                            variant="contained"
                                                            color="warning"
                                                            size="small"
                                                            onClick={() => handleOpenStatus(order)}
                                                        >
                                                            <PendingIcon fontSize="small" />
                                                        </ActionButton>
                                                    </Tooltip>
                                                    {hasPermission(PERMISSIONS.ALL) && (
                                                        <Tooltip title="Delete">
                                                            <ActionButton
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                onClick={() => handleOpenDelete(order)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </ActionButton>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                                            <Typography variant="subtitle1">
                                                {searchTerm || statusFilter !== 'all'
                                                    ? 'No matching orders found'
                                                    : 'No orders available'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filteredOrders.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                            <Pagination
                                count={count}
                                page={page}
                                onChange={handleChangePage}
                                color="primary"
                                shape="rounded"
                                showFirstButton
                                showLastButton
                            />
                        </div>
                    )}
                </div>
            </Fade>

            {/* ---------- VIEW DIALOG ---------- */}
            <Dialog
                open={openView}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                TransitionComponent={Slide}
                transitionDuration={300}
            >
                <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>Order Details</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingTop: '20px' }}>
                    {currentOrder && (
                        <Box>
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', mb: 2,
                                }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        LPO Number: {currentOrder.lpo_number}
                                    </Typography>
                                    <Chip
                                        label={(currentOrder.status || 'pending').toUpperCase()}
                                        color={getStatusColor(currentOrder.status)}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        Buyer Details
                                    </Typography>
                                    <Typography variant="body2"><strong>Name:</strong> {currentOrder.buyer_name}</Typography>
                                    <Typography variant="body2"><strong>Country:</strong> {currentOrder.buyer_country}</Typography>
                                    <Typography variant="body2"><strong>Destination:</strong> {currentOrder.buyer_destination}</Typography>
                                    <Typography variant="body2"><strong>Contact:</strong> {currentOrder.buyer_contact}</Typography>
                                    <Typography variant="body2"><strong>Email:</strong> {currentOrder.buyer_email || 'N/A'}</Typography>
                                    {/* ← NEW: Buyer TIN */}
                                    <Typography variant="body2"><strong>TIN No:</strong> {currentOrder.buyer_tin || 'N/A'}</Typography>
                                </Paper>
                            </Grid><br />

                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        Supplier Details
                                    </Typography>
                                    <Typography variant="body2"><strong>Country:</strong> {currentOrder.supplier_country}</Typography>
                                    <Typography variant="body2"><strong>Vendor Name:</strong> {currentOrder.supplier_vendor_name || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Location:</strong> {currentOrder.supplier_location || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Contact Person:</strong> {currentOrder.supplier_contact_person || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Account Name:</strong> {currentOrder.supplier_account_name || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Account No:</strong> {currentOrder.supplier_account_no || 'N/A'}</Typography>
                                </Paper>
                            </Grid><br />

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        Logistics Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Typography variant="body2"><strong>Requisitioner:</strong> {currentOrder.requisitioner_name}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2"><strong>Carriage:</strong> {currentOrder.carriage}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2"><strong>Incoterm:</strong> {currentOrder.incoterm}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2"><strong>Airport:</strong> {currentOrder.airport || 'N/A'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid><br />

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        Items
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>#</strong></TableCell>
                                                <TableCell><strong>Commodity</strong></TableCell>
                                                <TableCell><strong>Description</strong></TableCell>
                                                <TableCell><strong>Grade</strong></TableCell>
                                                <TableCell><strong>QTY</strong></TableCell>
                                                <TableCell><strong>Unit Price (UGX)</strong></TableCell>
                                                <TableCell><strong>Total (UGX)</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parseItems(currentOrder.items).map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>{getCommodityName(item)}</TableCell>
                                                    <TableCell>{item.description}</TableCell>
                                                    <TableCell>{item.grade}</TableCell>
                                                    <TableCell>{formatNumberWithComma(item.quantity)}</TableCell>
                                                    <TableCell>UGX {formatNumberWithComma(item.unitPrice)}</TableCell>
                                                    <TableCell>UGX {formatNumberWithComma(getItemTotal(item))}</TableCell>
                                                </TableRow>
                                            ))}
                                            {parseItems(currentOrder.items).length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                                                        No items recorded
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        Financial Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2"><strong>Subtotal:</strong> UGX {formatNumberWithComma(currentOrder.subtotal)}</Typography>
                                            <Typography variant="body2"><strong>Tax:</strong> UGX {formatNumberWithComma(currentOrder.tax)}</Typography>
                                            <Typography variant="body2"><strong>Shipping:</strong> UGX {formatNumberWithComma(currentOrder.shipping)}</Typography>
                                            <Typography variant="body2"><strong>Other:</strong> UGX {formatNumberWithComma(currentOrder.other)}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h6" color="primary">
                                                <strong>Total (UGX):</strong> UGX {formatNumberWithComma(currentOrder.total_ugx)}
                                            </Typography>
                                            <Typography variant="body2"><strong>Exchange Rate:</strong> {formatNumberWithComma(currentOrder.exchange_rate)}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions style={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px',
                }}>
                    <Button onClick={handleClose} color="primary" variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- STATUS DIALOG ---------- */}
            <Dialog
                open={openStatus}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Slide}
                transitionDuration={300}
            >
                <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>Update Order Status</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingTop: '20px' }}>
                    <Box>
                        <Grid item xs={12}>
                            <Typography variant="body2" style={{ marginBottom: '16px' }}>
                                <strong>LPO Number:</strong> {currentOrder?.lpo_number}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={statusFormData.status}
                                    onChange={handleStatusChange}
                                    label="Status"
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions style={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px',
                }}>
                    <Button onClick={handleClose} color="error" variant="outlined">Cancel</Button>
                    <Button onClick={handleStatusUpdate} color="primary" variant="contained">Update Status</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- DELETE DIALOG ---------- */}
            <Dialog
                open={openDelete}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Slide}
                transitionDuration={300}
            >
                <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
                    <Typography variant="body1" style={{ marginBottom: '16px' }}>
                        Are you sure you want to delete this order?
                    </Typography>
                    <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
                        {currentOrder?.lpo_number}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: '8px' }}>
                        Buyer: {currentOrder?.buyer_name}
                    </Typography>
                    <Typography variant="body2">
                        Total: UGX {formatNumberWithComma(currentOrder?.total_ugx)}
                    </Typography>
                    <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions style={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px',
                }}>
                    <Button onClick={handleClose} variant="outlined" color="primary">Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" startIcon={<DeleteIcon />}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- SNACKBAR ---------- */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default LpoTwoOrders;

// ---------------------------------------------------------------
// STYLED COMPONENTS
// ---------------------------------------------------------------
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
        transition: 'background-color 0.3s ease',
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(0.5),
    minWidth: 'auto',
}));